/**
 * Screen-position geometry for the click-to-place body-map diagram (`BACKLOG.md` M10a).
 *
 * `bodyMapping.ts` is explicit that it "deliberately knows nothing about" the diagram — this is
 * that diagram's own layer, built on top of it rather than inside it. It answers exactly one
 * question, as one small pure function: given a region, a side, and which view of the figure is
 * showing (`anterior`/`posterior`), where on that figure does the hotspot sit?
 *
 * **The bug this function exists not to reproduce**: `docs/comparison-emrx.md` §6.2. `emrx`'s
 * posterior-view coordinates for its `shoulderR`/`poplitealR` sites violate its own stated
 * left/right convention, because the mirroring fix was written as a flat "right = low x" rule
 * applied uniformly, rather than computed from `laterality` + `view` together. Its own closing
 * note names the general lesson: **laterality on a rendered figure is a view-dependent transform**.
 * `bodyMapLateralX` below is the fix applied as that lesson describes — four explicit, independently
 * stated branches (one per side/view combination), not one formula quietly reused across views —
 * and `bodyMapGeometry.test.ts` names the regression directly: a posterior-view *right*-sided point
 * must land at *high* x, the opposite of what the flat rule would have produced.
 *
 * **Convention** (matching `emrx`'s own stated one, `terminology.ts:184-195` per §6.2): in the
 * anterior view, the patient's LEFT renders at higher x. In the posterior view — the patient turned
 * around, viewed from behind — left and right visually swap, so the patient's LEFT renders at
 * *lower* x instead.
 *
 * Coordinates are plain numbers on a `0 0 100 200` SVG viewBox (`web/src/components/
 * BodyMapPicker.tsx` is the only consumer of the coordinate shape; this module has no UI dependency
 * of its own, matching `bodyMapping.ts`'s own layering).
 */

import { BODY_MAP_REGIONS, type BodyMapRegion, type BodyMapSide } from './bodyMapping.ts';

export type BodyMapView = 'anterior' | 'posterior';
export const BODY_MAP_VIEWS: readonly BodyMapView[] = ['anterior', 'posterior'];

export interface BodyMapPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * A region's x-geometry is either `midline` (one fixed x, no side distinction — matches
 * `bodyMapping.ts`'s own doc comment on which regions those are) or `lateral` (bilateral, defined by
 * the x the patient's **right** side renders at in the **anterior** view — always below 50 per the
 * convention above; every other side/view combination is derived from this one number by
 * `bodyMapLateralX`, never stored separately).
 */
type RegionX =
  | { readonly kind: 'midline'; readonly x: number }
  | { readonly kind: 'lateral'; readonly rightAnteriorX: number };

interface RegionGeometry {
  readonly x: RegionX;
  readonly y: number;
  /** Which view(s) show a hotspot for this region at all. A region with no natural back-view
   * presence (a facial feature, the front of the torso) simply has no posterior hotspot — the
   * picker is expected to fall back to the other view when the current region isn't visible in the
   * one currently showing (`BodyMapPicker.tsx`'s own view-switch effect). */
  readonly views: readonly BodyMapView[];
}

const ANTERIOR_ONLY: readonly BodyMapView[] = ['anterior'];
const POSTERIOR_ONLY: readonly BodyMapView[] = ['posterior'];
const BOTH_VIEWS: readonly BodyMapView[] = ['anterior', 'posterior'];

/**
 * One entry per `BodyMapRegion` — `Record<BodyMapRegion, RegionGeometry>` makes an added or removed
 * region a compile error here rather than a silent gap, the same discipline `bodyMapping.ts`'s own
 * header asks of `FACE_BODY_MAP_REGIONS` ("a region added here without a corresponding UI diagram
 * hotspot is a silent gap, not a free addition").
 */
const BODY_MAP_GEOMETRY: Record<BodyMapRegion, RegionGeometry> = {
  // Midline facial features — anterior view only.
  forehead: { x: { kind: 'midline', x: 50 }, y: 15, views: ANTERIOR_ONLY },
  glabella: { x: { kind: 'midline', x: 50 }, y: 19, views: ANTERIOR_ONLY },
  nose: { x: { kind: 'midline', x: 50 }, y: 24, views: ANTERIOR_ONLY },
  lips: { x: { kind: 'midline', x: 50 }, y: 31, views: ANTERIOR_ONLY },
  perioral: { x: { kind: 'midline', x: 50 }, y: 32, views: ANTERIOR_ONLY },
  chin: { x: { kind: 'midline', x: 50 }, y: 36, views: ANTERIOR_ONLY },

  // Bilateral facial features — anterior view only.
  brow: { x: { kind: 'lateral', rightAnteriorX: 44 }, y: 18, views: ANTERIOR_ONLY },
  crows_feet: { x: { kind: 'lateral', rightAnteriorX: 40 }, y: 20, views: ANTERIOR_ONLY },
  temple: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 17, views: ANTERIOR_ONLY },
  cheek: { x: { kind: 'lateral', rightAnteriorX: 40 }, y: 25, views: ANTERIOR_ONLY },
  nasolabial_fold: { x: { kind: 'lateral', rightAnteriorX: 42 }, y: 29, views: ANTERIOR_ONLY },
  marionette_lines: { x: { kind: 'lateral', rightAnteriorX: 44 }, y: 34, views: ANTERIOR_ONLY },

  // Head/neck regions visible from the front and the back.
  scalp: { x: { kind: 'midline', x: 50 }, y: 6, views: BOTH_VIEWS },
  jawline: { x: { kind: 'lateral', rightAnteriorX: 36 }, y: 33, views: BOTH_VIEWS },
  neck: { x: { kind: 'lateral', rightAnteriorX: 46 }, y: 42, views: BOTH_VIEWS },
  ear: { x: { kind: 'lateral', rightAnteriorX: 32 }, y: 22, views: BOTH_VIEWS },

  // Torso — front and back are genuinely different surfaces.
  chest: { x: { kind: 'midline', x: 50 }, y: 55, views: ANTERIOR_ONLY },
  abdomen: { x: { kind: 'midline', x: 50 }, y: 70, views: ANTERIOR_ONLY },
  back: { x: { kind: 'midline', x: 50 }, y: 60, views: POSTERIOR_ONLY },
  buttock: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 88, views: POSTERIOR_ONLY },

  // Limbs — visible from the front and the back alike. shoulder/elbow/wrist/hip/knee/ankle added for
  // BACKLOG.md M7 (orthopedics) — see bodyMapping.ts's own header on why. Each sits between its two
  // neighboring pre-existing joints/segments on the y-axis, at a similar rightAnteriorX to the limb
  // segment it joins, so the added hotspot lands in an anatomically sensible spot on the same
  // simplified figure rather than overlapping an existing one.
  shoulder: { x: { kind: 'lateral', rightAnteriorX: 24 }, y: 50, views: BOTH_VIEWS },
  upper_arm: { x: { kind: 'lateral', rightAnteriorX: 22 }, y: 58, views: BOTH_VIEWS },
  elbow: { x: { kind: 'lateral', rightAnteriorX: 18 }, y: 68, views: BOTH_VIEWS },
  forearm: { x: { kind: 'lateral', rightAnteriorX: 16 }, y: 78, views: BOTH_VIEWS },
  wrist: { x: { kind: 'lateral', rightAnteriorX: 13 }, y: 88, views: BOTH_VIEWS },
  hand: { x: { kind: 'lateral', rightAnteriorX: 12 }, y: 95, views: BOTH_VIEWS },
  hip: { x: { kind: 'lateral', rightAnteriorX: 36 }, y: 100, views: BOTH_VIEWS },
  thigh: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 115, views: BOTH_VIEWS },
  knee: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 132, views: BOTH_VIEWS },
  lower_leg: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 150, views: BOTH_VIEWS },
  ankle: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 165, views: BOTH_VIEWS },
  foot: { x: { kind: 'lateral', rightAnteriorX: 38 }, y: 175, views: BOTH_VIEWS },

  // Catch-all — no real anatomical laterality, kept as a small fixed marker off to the side rather
  // than overlapping a real region, present in both views.
  other: { x: { kind: 'midline', x: 92 }, y: 8, views: BOTH_VIEWS },
};

/**
 * The laterality transform. Four explicit, independently-stated branches — one per (side, view)
 * combination — rather than a single mirroring formula applied uniformly across both views. This is
 * deliberate: `docs/comparison-emrx.md` §6.2's bug is exactly what a shared, uniformly-applied
 * formula produces once one of its four cases is actually different from the other three, and it is
 * easy to get that from a compact expression without noticing. Each branch here can be read, and
 * tested, on its own.
 */
export function bodyMapLateralX(
  rightAnteriorX: number,
  side: 'left' | 'right',
  view: BodyMapView,
): number {
  if (view === 'anterior' && side === 'right') return rightAnteriorX;
  if (view === 'anterior' && side === 'left') return 100 - rightAnteriorX;
  if (view === 'posterior' && side === 'right') return 100 - rightAnteriorX;
  if (view === 'posterior' && side === 'left') return rightAnteriorX;
  // Exhaustive over the only two `BodyMapView` values and the only two lateral `side` values this
  // function accepts — unreachable, but a thrown error beats a silent `undefined` if that ever
  // stops being true.
  throw new Error(`unreachable body-map side/view combination: side=${side}, view=${view}`);
}

/**
 * Whether `region` has any hotspot at all in `view`. The picker uses this to decide whether it needs
 * to switch views when the caller's current region changes to one the currently-shown view doesn't
 * have (e.g. selecting `back` while the anterior view is showing).
 */
export function bodyMapRegionHasView(region: BodyMapRegion, view: BodyMapView): boolean {
  return BODY_MAP_GEOMETRY[region].views.includes(view);
}

/** Whether `region` is midline (no left/right distinction — the picker shows no side control for
 * one of these). Derived from the same geometry table `getBodyMapPosition` reads, so the two can
 * never disagree with each other. */
export function isMidlineBodyMapRegion(region: BodyMapRegion): boolean {
  return BODY_MAP_GEOMETRY[region].x.kind === 'midline';
}

/**
 * The screen position for `(region, side, view)`, or `null` if `region` has no hotspot in `view` at
 * all (the caller should not be rendering it there). For a bilateral region with no side chosen yet
 * (`side` is `null` or `'midline'`), returns the region's own centre line rather than guessing a
 * side — there is no correct single point for "unspecified."
 */
export function getBodyMapPosition(
  region: BodyMapRegion,
  side: BodyMapSide | null,
  view: BodyMapView,
): BodyMapPoint | null {
  const geometry = BODY_MAP_GEOMETRY[region];
  if (!geometry.views.includes(view)) return null;
  if (geometry.x.kind === 'midline') {
    return { x: geometry.x.x, y: geometry.y };
  }
  if (side !== 'left' && side !== 'right') {
    return { x: 50, y: geometry.y };
  }
  return { x: bodyMapLateralX(geometry.x.rightAnteriorX, side, view), y: geometry.y };
}

/** One clickable hotspot on one view of the figure: a region (midline: one hotspot; bilateral: one
 * per side), its resolved position, and the side it represents (`null` for a midline hotspot). */
export interface BodyMapHotspot {
  readonly region: BodyMapRegion;
  readonly side: 'left' | 'right' | null;
  readonly point: BodyMapPoint;
}

/** Every hotspot the picker should render for `view`, in `BODY_MAP_REGIONS`' own declared order —
 * a stable, deterministic render/tab order rather than one that depends on object key iteration. */
export function bodyMapHotspotsForView(view: BodyMapView): readonly BodyMapHotspot[] {
  const hotspots: BodyMapHotspot[] = [];
  for (const region of BODY_MAP_REGIONS) {
    const geometry = BODY_MAP_GEOMETRY[region];
    if (!geometry.views.includes(view)) continue;
    if (geometry.x.kind === 'midline') {
      hotspots.push({ region, side: null, point: { x: geometry.x.x, y: geometry.y } });
      continue;
    }
    const rightAnteriorX = geometry.x.rightAnteriorX;
    hotspots.push({
      region,
      side: 'right',
      point: { x: bodyMapLateralX(rightAnteriorX, 'right', view), y: geometry.y },
    });
    hotspots.push({
      region,
      side: 'left',
      point: { x: bodyMapLateralX(rightAnteriorX, 'left', view), y: geometry.y },
    });
  }
  return hotspots;
}
